import { useRef, useState } from 'react';
import { useFocusTrap } from '../motion';
import { type Issue } from '../types';
import { type Leftovers, type Sprint } from '../sprints';
import { asDayMonth } from '../utils';

/**
 * WHAT HAPPENS TO WORK THAT DID NOT GET FINISHED.
 *
 * ASKED RATHER THAN DECIDED — Vinnie, 13 August: "why can't we have a pop-up
 * that asks the developer whether to carry it forward or send it to the
 * backlog? Let it be the user's choice."
 *
 * WHY IT IS A QUESTION AND NOT A SETTING. The two answers mean different things
 * about the work rather than expressing a preference: carrying it says the team
 * is still committed and the fortnight ran out; sending it back says the
 * commitment lapsed with the sprint. Velocity, the sprint report and the
 * say-do ratio every retrospective asks about all read differently depending
 * which was true — and only the people closing the sprint know which it was.
 * A setting would answer it once, in advance, for every fortnight after.
 *
 * ONLY WHEN THERE IS SOMETHING TO ASK. A sprint that finished everything closes
 * on the press, with no dialog: a confirmation that can only be answered one way
 * is how people learn to press through the ones that matter.
 *
 * NO CLICK-AWAY CLOSE, for the same reason as WhyClosed — this asks a question,
 * and dismissing it by missing the box is how somebody answers it by accident.
 */
export default function FinishSprint({
  sprint,
  left,
  into,
  busy,
  problem,
  choose,
  cancel,
}: {
  sprint: Sprint;
  /** The cards that did not get finished. Never empty — see above. */
  left: Issue[];
  /** The sprint they would be carried into, if one is planned. */
  into?: Sprint;
  busy: boolean;
  problem: string;
  /** The answer, plus whether to start the next sprint in the same breath. */
  choose: (what: Leftovers, alsoStart: boolean) => void;
  cancel: () => void;
}) {
  const [what, setWhat] = useState<Leftovers>(into ? 'carry' : 'backlog');
  /*
   * AND START THE NEXT ONE — the other half of "closing one and opening the
   * next". Offered here because that IS the fortnight boundary: a team closes,
   * decides where the leftovers go, and starts the next one, and making them
   * find a second button afterwards turns one ceremony into two errands.
   *
   * OFF BY DEFAULT, though, and that is deliberate rather than timid. Plenty of
   * teams close on Friday for a sprint that begins on Monday, and a tick that
   * starts a fortnight early is a wrong start date on every chart drawn from
   * it — for a sprint that then cannot be un-started. The dates are printed
   * beside it so an early start is visible before it is chosen.
   */
  const [alsoStart, setAlsoStart] = useState(false);
  const many = left.length !== 1;
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef);

  return (
    <div
      className="overlay setup-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Finishing ${sprint.name}`}
    >
      <div ref={panelRef} tabIndex={-1} className="feedback">
        <h2>
          Finishing {sprint.name} — {left.length} {many ? 'cards are' : 'card is'} unfinished
        </h2>
        <p className="hint">
          Where {many ? 'they' : 'it'} should go decides what this sprint&apos;s numbers mean
          afterwards, so it is worth a moment. Nothing is deleted either way.
        </p>

        <div className="mode-cards">
          <button
            className={`mode-card ${what === 'carry' ? 'is-on' : ''}`}
            /*
             * OFFERED AND REFUSED, RATHER THAN MISSING. A choice that vanishes
             * looks like software that only supports the other one — this says
             * why it cannot be taken, which is a thing they can go and fix.
             */
            disabled={!into || busy}
            onClick={() => setWhat('carry')}
          >
            <span className="mode-card-title">
              {into ? `Carry into ${into.name}` : 'Carry into the next sprint'}
              {what === 'carry' && <span className="person-you">chosen</span>}
            </span>
            <span className="mode-card-blurb">
              {into
                ? `The work is still committed — the fortnight ran out, not the intent. It counts as spilled over on this sprint's report and as committed to ${into.name}.`
                : 'There is no planned sprint to carry into. Plan one first if this is what you want.'}
            </span>
          </button>

          <button
            className={`mode-card ${what === 'backlog' ? 'is-on' : ''}`}
            disabled={busy}
            onClick={() => setWhat('backlog')}
          >
            <span className="mode-card-title">
              Send back to the backlog
              {what === 'backlog' && <span className="person-you">chosen</span>}
            </span>
            <span className="mode-card-blurb">
              The commitment ends with the sprint. It goes back in the pile to be picked up — or
              not — when the next one is planned.
            </span>
          </button>
        </div>

        {/* NAMED, NOT COUNTED. "3 cards" is a number; the references are what
            somebody checks against their own memory of the fortnight, and it is
            the last moment before they move. */}
        <p className="hint">
          {left.map((i) => i.ref).join(', ')}
        </p>

        {problem && (
          <p className="login-error" role="alert">
            {problem}
          </p>
        )}

        {into && (
          <label className="grp-row sprint-also">
            <input
              type="checkbox"
              checked={alsoStart}
              disabled={busy}
              onChange={(e) => setAlsoStart(e.target.checked)}
            />
            <span className="grp-name">Start {into.name} straight away</span>
            <span className="grp-sub">
              {asDayMonth(into.startsOn)} to {asDayMonth(into.endsOn)}
            </span>
          </label>
        )}

        <div className="feedback-foot">
          <button className="btn" onClick={cancel} disabled={busy}>
            {/* "Cancel" is honest here, unlike in WhyClosed: nothing has happened
                yet and the sprint is still running. */}
            Cancel
          </button>
          <button className="btn btn-primary" onClick={() => choose(what, alsoStart && !!into)} disabled={busy}>
            {busy ? 'Finishing…' : 'Finish the sprint'}
          </button>
        </div>
      </div>
    </div>
  );
}
